import React, { useState, useEffect } from "react";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { Form, Button } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { SendOTP, VerifyOTP } from "../../../../features/auth/Slice";

const OTP_TIMER = 60;

function Email_Reset({ register, errors, watch, setValue }) {
  const dispatch = useDispatch();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [emailLocked, setEmailLocked] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [otpError, setOtpError] = useState("");
  const [countdown, setCountdown] = useState(0);

  // Countdown timer
  useEffect(() => {
    if (countdown <= 0) return;
    const timer = setTimeout(() => setCountdown((prev) => prev - 1), 1000);
    return () => clearTimeout(timer);
  }, [countdown]);

  const resetOTPState = () => {
    setOtpSent(false);
    setOtpVerified(false);
    setOtpError("");
    setCountdown(0);
    setValue("otp", "");
  };

  const handleChangeEmail = () => {
    setEmailLocked(false);
    resetOTPState();
  };

  const handleSendOTP = async () => {
    const email = watch("email");
    const emailRegex = /^[a-zA-Z0-9]+([._%+-]?[a-zA-Z0-9]+)*@[a-zA-Z0-9-]+(\.[a-zA-Z]{2,})+$/;
    if (!email || !emailRegex.test(email)) {
      setOtpError("Please enter a valid email first");
      return;
    }
    setOtpError("");
    setOtpLoading(true);
    try {
      const formData = new FormData();
      formData.append("email", email);
      formData.append("type", "email");

      const res = await dispatch(SendOTP(formData));

      if (res.meta?.requestStatus === "fulfilled") {
        setEmailLocked(true);
        setOtpSent(true);
        setOtpVerified(false);
        setCountdown(OTP_TIMER);
        setOtpError("");
      } else {
        setOtpError(res?.payload?.errors || "Failed to send OTP. Try again.");
      }
    } catch (err) {
      setOtpError("Something went wrong.");
    } finally {
      setOtpLoading(false);
    }
  };

  const handleVerifyOTP = async () => {
    const otp = watch("otp");
    const email = watch("email");
    if (!otp || otp.length < 4) {
      setOtpError("Please enter the OTP");
      return;
    }
    setOtpError("");
    setVerifyLoading(true);
    try {
      const formData = new FormData();
      formData.append("email", email);
      formData.append("otp", otp);
      formData.append("type", "email");

      const res = await dispatch(VerifyOTP(formData));

      if (res.meta?.requestStatus === "fulfilled") {
        setOtpVerified(true);
        setOtpError("");
      } else {
        setOtpError(res?.payload?.errors || "Invalid OTP. Try again.");
      }
    } catch (err) {
      setOtpError("Something went wrong.");
    } finally {
      setVerifyLoading(false);
    }
  };

  return (
    <>
      {/* Email */}
      <div className="form-group field_holder">
        <Form.Label className="d-flex justify-content-between align-items-center">
          <span>
            <img
              src="/assets/images/icons/phone.png"
              alt="icon"
              className="login_lable_icons"
            />{" "}
            Email
          </span>
          {/* Change Email link — shown when email is locked */}
          {emailLocked && (
            <small
              className="text-primary"
              style={{ cursor: "pointer", fontSize: "12px" }}
              onClick={handleChangeEmail}
            >
              Change Email
            </small>
          )}
        </Form.Label>

        <Form.Control
          type="email"
          placeholder="Please enter the Email"
          disabled={emailLocked}
          {...register("email", {
            required: "Email is required",
            pattern: {
              value: /^[a-zA-Z0-9]+([._%+-]?[a-zA-Z0-9]+)*@[a-zA-Z0-9-]+(\.[a-zA-Z]{2,})+$/,
              message: "Enter a valid email address",
            },
          })}
        />
        {errors.email && (
          <small className="text-danger">{errors.email.message}</small>
        )}
      </div>

      {/* Set Password */}
      <div className="form-group field_holder">
        <Form.Label>
          <img
            src="/assets/images/icons/lock.png"
            alt="icon"
            className="login_lable_icons"
          />{" "}
          Set Password
        </Form.Label>
        <div className="password_input">
          <Form.Control
            type={showPassword ? "text" : "password"}
            placeholder="Set Password"
            {...register("password", {
              required: "Password is required",
              minLength: {
                value: 6,
                message: "Password must be at least 6 characters",
              },
            })}
          />
          <span className="eye-icon" onClick={() => setShowPassword(!showPassword)}>
            {showPassword ? <FaEye /> : <FaEyeSlash />}
          </span>
        </div>
        {errors.password && (
          <small className="text-danger">{errors.password.message}</small>
        )}
      </div>

      {/* Confirm Password */}
      <div className="form-group field_holder">
        <Form.Label>
          <img
            src="/assets/images/icons/lock.png"
            alt="icon"
            className="login_lable_icons"
          />{" "}
          Confirm Password
        </Form.Label>
        <div className="password_input">
          <Form.Control
            type={showConfirmPassword ? "text" : "password"}
            placeholder="Confirm Password"
            {...register("confirm_password", {
              required: "Please confirm your password",
              validate: (value) =>
                value === watch("password") || "Passwords do not match",
            })}
          />
          <span className="eye-icon" onClick={() => setShowConfirmPassword(!showConfirmPassword)}>
            {showConfirmPassword ? <FaEye /> : <FaEyeSlash />}
          </span>
        </div>
        {errors.confirm_password && (
          <small className="text-danger">{errors.confirm_password.message}</small>
        )}
      </div>

      {/* OTP */}
      <div className="form-group field_holder invite_code_wrapper">
        <Form.Label>
          <img
            src="/assets/images/icons/sheild.png"
            alt="icon"
            className="login_lable_icons"
          />{" "}
          OTP
          {otpVerified && <small className="text-success ms-2">✔ Verified</small>}
        </Form.Label>

        <div className="invite_input">
          <Form.Control
            type="text"
            placeholder="Please Enter the OTP"
            disabled={otpVerified}
            {...register("otp", {
              required: "OTP is required",
              minLength: { value: 4, message: "Enter a valid OTP" },
            })}
          />

          {/*
            Flow:
            1. Not sent yet           → "Send OTP"
            2. Sent + timer running   → "Verify OTP (Xs)"
            3. Timer expired          → "Resend OTP"
            4. Verified               → button hidden
          */}
          {!otpVerified && (
            !otpSent || countdown === 0 ? (
              <Button
                className="send_btn"
                type="button"
                onClick={handleSendOTP}
                disabled={otpLoading}
              >
                {otpLoading ? "Sending..." : otpSent ? "Resend OTP" : "Send OTP"}
              </Button>
            ) : (
              <Button
                className="send_btn"
                type="button"
                onClick={handleVerifyOTP}
                disabled={verifyLoading}
              >
                {verifyLoading ? "Verifying..." : `Verify OTP (${countdown}s)`}
              </Button>
            )
          )}
        </div>

        {otpError && <small className="text-danger d-block mt-1">{otpError}</small>}
        {errors.otp && <small className="text-danger d-block">{errors.otp.message}</small>}
      </div>
    </>
  );
}

export default Email_Reset;